"use client";

import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { PropertyType } from "@/types";

// Konaklama tipleri
const PROPERTY_TYPES: { value: PropertyType; label: string }[] = [
  { value: "villa", label: "Villa" },
  { value: "apart", label: "Apart" },
  { value: "bungalov", label: "Bungalov" },
  { value: "daire", label: "Daire" },
  { value: "yazlik", label: "Yazlık" },
  { value: "other", label: "Diğer" },
];

const SORT_OPTIONS = [
  { value: "recommended", label: "Önerilenler" },
  { value: "price_asc", label: "Fiyat (Düşükten Yükseğe)" },
  { value: "price_desc", label: "Fiyat (Yüksekten Düşüğe)" },
  { value: "newest", label: "En Yeni" },
];

interface ListingsFiltersProps {
  isMobile?: boolean;
}

export function ListingsFilters({ isMobile = false }: ListingsFiltersProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentSort = searchParams.get("sort") || "recommended";

  const updateSearchParams = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams.toString());

    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }

    params.delete("page");

    router.push(`${pathname}?${params.toString()}`);
  };

  if (isMobile) {
    return (
      <div className="scrollbar-hide flex items-center justify-end gap-2 overflow-x-auto border-b px-4 py-2">
        {/* Sıralama */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm">
              Sırala
              <ChevronDown className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SORT_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => updateSearchParams("sort", option.value)}
                className={cn(currentSort === option.value && "bg-muted")}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  // Desktop
  return (
    <>
      {/* Sıralama (sağ üst) */}
      <Select value={currentSort} onValueChange={(value) => updateSearchParams("sort", value)}>
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Sırala" />
        </SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}

// Küçük yuvarlak sıralama butonu — header'daki arama barının yanında (masaüstü +
// mobil). Tıklanınca sıralama seçeneklerini açar. (Eski büyük sıralama çubuğunun
// yerini alır; sayfa üstündeki geri/özet/sayı alanı tamamen kaldırıldı.)
export function SortIconButton({ className }: { className?: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSort = searchParams.get("sort") || "recommended";

  const updateSort = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("sort", value);
    params.delete("page");
    router.push(`${pathname}?${params.toString()}`);
  };

  const active = currentSort !== "recommended";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Sırala"
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border bg-white transition-colors",
            active ? "border-primary text-primary" : "border-border hover:border-primary",
            className
          )}
        >
          <SlidersHorizontal className="h-5 w-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
        {SORT_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => updateSort(option.value)}
            className={cn("rounded-lg", currentSort === option.value && "bg-muted font-medium")}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PropertyTypeFilters() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentTypes = searchParams.get("propertyType")?.split(",").filter(Boolean) || [];

  const togglePropertyType = (type: PropertyType) => {
    const params = new URLSearchParams(searchParams.toString());
    let newTypes: string[];

    if (currentTypes.includes(type)) {
      newTypes = currentTypes.filter((t) => t !== type);
    } else {
      newTypes = [...currentTypes, type];
    }

    if (newTypes.length > 0) {
      params.set("propertyType", newTypes.join(","));
    } else {
      params.delete("propertyType");
    }

    params.delete("page");
    router.push(`${pathname}?${params.toString()}`);
  };

  return (
    <div className="mt-4 flex flex-wrap gap-2">
      {PROPERTY_TYPES.map((type) => (
        <button
          key={type.value}
          onClick={() => togglePropertyType(type.value)}
          className={cn(
            "rounded-full border px-4 py-2 text-sm transition-colors",
            currentTypes.includes(type.value)
              ? "border-primary bg-primary text-white"
              : "border-border hover:border-primary"
          )}
        >
          {type.label}
        </button>
      ))}
    </div>
  );
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
}

export function Pagination({ currentPage, totalPages }: PaginationProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (totalPages <= 1) return null;

  const goToPage = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", page.toString());
    router.push(`${pathname}?${params.toString()}`);
  };

  // Sayfa numaralarını hesapla
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div className="mt-12 flex justify-center">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          disabled={currentPage <= 1}
          onClick={() => goToPage(currentPage - 1)}
        >
          Önceki
        </Button>

        {getPageNumbers().map((page, index) =>
          typeof page === "number" ? (
            <Button
              key={index}
              variant="outline"
              size="icon"
              className={cn(currentPage === page && "bg-primary hover:bg-primary/90 text-white")}
              onClick={() => goToPage(page)}
            >
              {page}
            </Button>
          ) : (
            <span key={index} className="px-2">
              ...
            </span>
          )
        )}

        <Button
          variant="outline"
          disabled={currentPage >= totalPages}
          onClick={() => goToPage(currentPage + 1)}
        >
          Sonraki
        </Button>
      </div>
    </div>
  );
}
